import React, { useEffect, useState } from 'react'
import {
  Box,
  CircularProgress,
  FormLabel,
  Grid,
  Modal,
  styled,
  Typography,
  Button,
  DialogActions,
  LinearProgress,
  Chip,
  Avatar,
  Paper,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider,
  DialogTitle,
  Dialog,
  DialogContent,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import lightTheme from '../../styles/theme/lightTheme'
import '../../styles/components/_box.scss'
import '../../styles/components/_grids.scss'
import '../../styles/components/_inputs.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCalendarPlus,
  faChainBroken,
  faCircleCheck,
  faCircleExclamation,
  faClose,
  faHeart,
  faHeartBroken,
  faGraduationCap,
  faBriefcase,
  faLanguage,
  faChartLine,
  faBrain,
  faMapMarkerAlt,
  faEnvelope,
  faPhone,
  faGlobe,
  faHandshake,
  faCalendarAlt,
  faArrowLeft,
  faPaperPlane,
  faClock,
  faVideo,
  faInfoCircle,
} from '@fortawesome/pro-solid-svg-icons'
import { styled as muiStyled } from '@mui/material/styles'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import * as yup from 'yup'
import Api from '../../services/api.service'
import { handlingErrors } from '../../utils'
import { toast, ToastContainer, Slide } from 'react-toastify'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  disponibilitesList2,
  disponibleSousList2,
  niveauCompetencesList,
  niveauLanguesList,
} from '../../data'
import moment from 'moment-timezone'
import { format } from 'date-fns'
import dayjs from 'dayjs'
import { faChain, faXmark } from '@fortawesome/pro-regular-svg-icons'
import ButtonRadioInput2 from '../inputs/ButtonRadioInput2'
import GradientButton from '../buttons/GradientButton'
import OutlineButton from '../buttons/OutlineButton'

const ModalContainer = styled(Box)(({ theme }) => ({
  width: 'calc(100% - 50px)',
  height: 'calc(100vh - 50px)',
  maxWidth: '900px',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  padding: '30px',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  overflow: 'auto',
  maxHeight: '90vh',
}))

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  padding: '20px',
  background:
    'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
  borderRadius: '12px',
  border: '1px solid rgba(102, 126, 234, 0.15)',
}))

const InfoCard = styled(Card)(({ theme }) => ({
  marginBottom: '16px',
  borderRadius: '12px',
  border: '1px solid rgba(102, 126, 234, 0.15)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}))

const ScoreChip = styled(Chip)(({ theme, score }) => {
  const getColor = (score) => {
    if (score >= 80) return { bg: '#4caf50', color: 'white' }
    if (score >= 60) return { bg: '#ff9800', color: 'white' }
    if (score >= 40) return { bg: '#f44336', color: 'white' }
    return { bg: '#9e9e9e', color: 'white' }
  }

  const colors = getColor(score)

  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 'bold',
    fontSize: '14px',
    height: '32px',
    '& .MuiChip-label': {
      fontWeight: 'bold',
    },
  }
})

const SkillChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(99, 102, 241, 0.08)',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  color: '#6366f1',
  fontWeight: 600,
  fontSize: '0.875rem',
  letterSpacing: '0.5px',
  '&:hover': {
    backgroundColor: 'rgba(99, 102, 241, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const HTMLContent = muiStyled('div')(({ theme }) => ({
  '& p': {
    marginBottom: '16px',
    lineHeight: 1.6,
    color: '#4b5563',
    fontSize: '1rem',
  },
  '& strong': {
    fontWeight: 600,
    color: '#1a1a1a',
  },
  '& ul': {
    paddingLeft: '20px',
    marginBottom: '16px',
  },
  '& li': {
    marginBottom: '8px',
    lineHeight: 1.5,
    color: '#4b5563',
  },
}))

const LanguageChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(16, 185, 129, 0.08)',
  border: '1px solid rgba(16, 185, 129, 0.2)',
  color: '#10b981',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const SoftSkillChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(245, 158, 11, 0.08)',
  border: '1px solid rgba(245, 158, 11, 0.2)',
  color: '#f59e0b',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const ValueChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(139, 92, 246, 0.08)',
  border: '1px solid rgba(139, 92, 246, 0.2)',
  color: '#8b5cf6',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(139, 92, 246, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const ProgressSection = styled(Box)(({ theme }) => ({
  marginBottom: '12px',
}))

const ProgressLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  marginBottom: '4px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

const schema = yup.object({})

const DetailCandidatEnhanced = ({
  open,
  onClose,
  onSuccess,
  candidat,
  companyId,
  poste,
  besoinEntrepriseId,
  entrepriseId,
  isAdmin = false,
  ids,
}) => {
  const { t, i18n } = useTranslation()
  const currentUser = useSelector((state) => state.userAuth.user)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const sizeChip = useMediaQuery(theme.breakpoints.down('sm')) ? 'small' : 'medium'
  const [loading, setLoading] = useState(false)
  const [loadingMatch, setLoadingMatch] = useState(false)
  const [step, setStep] = useState(1)
  const [errorDate, setErrorDate] = useState(false)
  const [errorTime, setErrorTime] = useState(false)
  const [isFavorie, setIsFavorie] = useState(false)
  const [idFavori, setIdFavori] = useState(null)
  const [seeMore, setSeeMore] = useState(false)
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [slots, setSlots] = useState([])
  const [times, setTimes] = useState('')

  // Get candidate data from nested structure
  const candidate = candidat?.candidate.candidat || candidat
  const scores = candidat?.scores || {}
  const overallScore = candidat?.overallScore || 0

  const handleSeeMore = (event) => {
    setSeeMore(!seeMore)
  }

  const addToFavorie = async () => {
    try {
      const result = await Api.favoris.add({
        utilisateur: '/api/utilisateurs/' + currentUser.data.id,
        favori: '/api/utilisateurs/' + candidat?.candidate?.id,
      })
      const res = handlingErrors(result)

      if (res.ok) {
        setIsFavorie(true)
        setIdFavori(res.data.id)
        toast.success(t('successMessage.candidateAddedToFavorite'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleCheck}
              fontSize={25}
              color={lightTheme.palette.success.main}
            />
          ),
        })
      }

      if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
        window.scrollTo(0, 0)
        return
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
    }
    setLoading(false)
  }

  const removeFromFavorie = async () => {
    try {
      const result = await Api.favoris.delete(idFavori)
      const res = handlingErrors(result)

      if (res.ok) {
        setIsFavorie(false)
        toast.success(t('successMessage.candidateRemovedFromFavorite'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleCheck}
              fontSize={25}
              color={lightTheme.palette.success.main}
            />
          ),
        })
      }

      if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
        window.scrollTo(0, 0)
        return
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
    }
    setLoading(false)
  }

  const addCandidatMatch = async () => {
    try {
      const result = await Api.matching.add({
        besoin: '/api/besoin_entreprises/' + besoinEntrepriseId,
        candidat: '/api/candidats/' + candidate.id,
      })
      const res = handlingErrors(result)

      if (res.ok) {
        toast.success(t('successMessage.candidateAdded'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleCheck}
              fontSize={25}
              color={lightTheme.palette.success.main}
            />
          ),
        })
      }

      if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
        window.scrollTo(0, 0)
        return
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
    }
    setLoadingMatch(false)
  }

  const removeCandidatMatch = async () => {
    let data = {
      ids: [isAdmin ? candidat?.candidate?.id : candidat?.candidate?.id],
    }

    /* console.log(besoinEntrepriseId, data)
    return */
    setLoadingMatch(true)
    //return

    try {
      const result = await Api.besoinEntreprise.edit(besoinEntrepriseId, data)
      const res = handlingErrors(result)

      if (res.ok) {
        toast.success(t('successMessage.candidateRemoveMatch'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleCheck}
              fontSize={25}
              color={lightTheme.palette.success.main}
            />
          ),
        })
        onSuccess()
      }

      if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
        window.scrollTo(0, 0)
        return
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
    }
    setLoadingMatch(false)
  }

  const onSubmit = async (data) => {
    if (dateValue) {
      setErrorDate(false)
    } else {
      setErrorDate(true)
    }

    if (times) {
      setErrorTime(false)
    } else {
      setErrorTime(true)
    }

    if (!dateValue || !times) return

    const DateValue = format(new Date(dateValue), 'yyyy-MM-dd')

    data.dateDebut = `${DateValue}T${times}`
    data.dateFin = `${DateValue}T${times}`
    data.detail = 'Proposition RDV'
    data.type = 'rdv_entreprise'
    data.entreprise = '/api/entreprises/' + companyId
    data.candidat = '/api/candidats/' + candidate.id

    setLoading(true)

    try {
      const result = await Api.calendar.add(data)
      const res = handlingErrors(result)

      if (res.ok) {
        toast.success(t('successMessage.appointmentRequestSent'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleCheck}
              fontSize={25}
              color={lightTheme.palette.success.main}
            />
          ),
        })
      }

      if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
        window.scrollTo(0, 0)
        return
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
    }
    setLoading(false)
  }

  const currentDate = new Date()
  const tomorrowDate = new Date()
  tomorrowDate.setDate(currentDate.getDate() + 1)
  const [dateValue, setDateValue] = useState(dayjs(tomorrowDate))
  const [currentdate, setCurrentDate] = useState(dayjs(tomorrowDate))
  const { handleSubmit } = useForm({
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    chackFavorie()
  }, [candidate.id])

  useEffect(() => {
    getDateDisponible()
  }, [dateValue])

  const chackFavorie = async () => {
    try {
      const result = await Api.favoris.listWithFilter({
        utilisateur: currentUser.data.id,
        favori: candidat?.candidate?.id,
      })
      const res = handlingErrors(result)

      if (res.ok) {
        if (res.data.length > 0) {
          setIsFavorie(true)
          setIdFavori(res.data[0].id)
        }
      }

      if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
    }
  }

  const getScoreColor = (score) => {
    if (score >= 80) return '#4caf50'
    if (score >= 60) return '#ff9800'
    if (score >= 40) return '#f44336'
    return '#9e9e9e'
  }

  const formatScore = (score) => {
    return Math.round(score)
  }

  if (!candidate) {
    return null
  }

  const handleChangeButtonRadio = (inputValue, dateValue) => {
    setTimes(inputValue)
    setCurrentDate(dateValue)
  }

  const getAvailabilityLabel = () => {
    const mainDispo =
      disponibilitesList2.find(
        (dispo) => dispo.id === candidate.typeDisponibilite
      )?.titre[i18n.language] || ''

    let additionalInfo = ''

    if (candidate.typeDisponibilite === 2 && candidate.disponibleSous) {
      const sousDispo = disponibleSousList2.find(
        (dispoS) => dispoS.id === candidate.disponibleSous
      )?.titre[i18n.language]
      additionalInfo = sousDispo ? ` ${sousDispo}` : ''
    }

    if (candidate.typeDisponibilite === 4 && candidate.disponible) {
      additionalInfo = ` ${moment(candidate.disponible)
        .tz('Europe/Paris')
        .format('DD/MM/YYYY')}`
    }

    return mainDispo + additionalInfo
  }

  const getDateDisponible = async () => {
    setLoadingSlots(true)
    setTimes('')
    const DateFormat = format(new Date(dateValue), 'yyyy-MM-dd')

    try {
      let result = await Api.calendar.listDIspoEntreprisePerDate(
        DateFormat,
        entrepriseId
      )
      const handling = handlingErrors(result)

      if (handling.status === 401) {
        setLoadingSlots(false)
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
        window.scrollTo(0, 0)
        return
      }

      if (handling.ok) {
        const res = handling.data

        //const DateValue = format(new Date(dateValue), 'dd/MM/yyyy')

        setSlots(res)
        setLoadingSlots(false)
      } else {
        setLoadingSlots(false)
        window.scrollTo(0, 0)
      }
    } catch (error) {
      setLoading(false)
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
      window.scrollTo(0, 0)
    }
  }



  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
        scroll={'paper'}
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : '16px',
            p: fullScreen ? 0 : '8px',
            m: fullScreen ? 0 : undefined,
            maxHeight: fullScreen ? '100%' : '90vh',
            ...(fullScreen && {
              display: 'flex',
              flexDirection: 'column',
            }),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
            backgroundColor: 'rgba(249, 250, 251, 0.5)',
          }}
        >
          <Typography
            variant={fullScreen ? 'subtitle1' : 'h6'}
            sx={{
              fontWeight: 600,
              color: '#1f2937',
              pr: 1,
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {step === 1
              ? t('box.title.candidateDetail')
              : t('box.title.rdvPoroposition')}
          </Typography>

          <IconButton onClick={onClose} sx={{ color: '#6B7280' }}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 4 },
            py: { xs: 2, sm: 3 },
            pt: { xs: '16px !important', sm: '32px !important' },
            flex: fullScreen ? 1 : undefined,
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              display: step === 1 ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            {/* Candidate Basic Info */}
            <InfoCard>
              <CardContent sx={{ padding: '24px' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'bold', mb: 1, color: '#1F2937' }}
                    >
                      {candidat.candidate?.prenom} {candidat.candidate?.nom}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        color: 'text.secondary',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          style={{
                            color: '#667eea',
                            fontSize: '14px',
                          }}
                        />
                        <Typography variant="body2">
                          {candidat.candidate?.email}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faPhone}
                          style={{
                            color: '#667eea',
                            fontSize: '14px',
                          }}
                        />
                        <Typography variant="body2">
                          {candidat.candidate?.telephone}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          style={{
                            color: '#667eea',
                            fontSize: '14px',
                          }}
                        />
                        <Typography variant="body2">
                          {candidat.candidate?.ville},{' '}
                          {candidat.candidate?.pays}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      marginLeft: 'auto',
                    }}
                  >
                    <ScoreChip
                      label={`${formatScore(overallScore)}% Match`}
                      score={overallScore}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                  {/* <SkillChip
                    icon={
                      <FontAwesomeIcon icon={faGraduationCap} fontSize={12} />
                    }
                    label={`${candidate?.competences?.length || 0} ${t(
                      'filter.competences'
                    )}`}
                    size={sizeChip}
                  />
                  <SkillChip
                    icon={<FontAwesomeIcon icon={faHeart} fontSize={12} />}
                    label={`${candidate?.competencesAnnexes?.length || 0} ${t(
                      'filter.competencesAnnexes'
                    )}`}
                    size={sizeChip}
                    sx={{
                      backgroundColor: 'rgba(245, 158, 11, 0.08)',
                      border: '1px solid rgba(245, 158, 11, 0.2)',
                      color: '#f59e0b',
                      '&:hover': {
                        backgroundColor: 'rgba(245, 158, 11, 0.12)',
                      },
                    }}
                  /> */}
                  <SkillChip
                    icon={
                      <FontAwesomeIcon
                        icon={faClock}
                        style={{
                          color: '#f59e0b',
                          fontSize: '18px',
                        }}
                      />
                    }
                    label={
                      candidate.typeTempsTravail === 'partiel'
                        ? t('box.timeWorkType2') +
                        ' (' +
                        candidate.tempPartiel +
                        '%)'
                        : t('box.timeWorkType1')
                    }
                    size={sizeChip}
                    sx={{
                      borderColor: 'rgba(255, 152, 0, 0.3)',
                      color: '#ff9800',
                      backgroundColor: 'rgba(255, 152, 0, 0.05)',
                    }}
                  />
                  <SkillChip
                    icon={
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        style={{
                          color: '#8b5cf6',
                          fontSize: '18px',
                          marginLeft: '10px',
                        }}
                      />
                    }
                    label={getAvailabilityLabel()}
                    size={sizeChip}
                    sx={{
                      borderColor: 'rgba(139, 92, 246, 0.3)',
                      color: '#8b5cf6',
                      backgroundColor: 'rgba(139, 92, 246, 0.05)',
                    }}
                  />
                </Box>
              </CardContent>
            </InfoCard>

            <InfoCard>
              <CardContent sx={{ padding: '20px' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FontAwesomeIcon icon={faBriefcase} color="#667eea" />
                  {t('profile.candidat.descriptif')}
                </Typography>

                <HTMLContent
                  dangerouslySetInnerHTML={{
                    __html: candidate.resume || '',
                  }}
                />
              </CardContent>
            </InfoCard>

            {/* Score Breakdown */}
            <InfoCard>
              <CardContent sx={{ padding: '20px' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FontAwesomeIcon icon={faChartLine} color="#667eea" />
                  {t('filter.matching')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={6}>
                    <ProgressSection>
                      <ProgressLabel>
                        <span>{t('filter.competences')}</span>
                        <span>
                          {formatScore(scores.competences?.percentage || 0)}%
                        </span>
                      </ProgressLabel>
                      <LinearProgress
                        variant="determinate"
                        value={scores.competences?.percentage || 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getScoreColor(
                              scores.competences?.percentage || 0
                            ),
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {scores.competences?.score || 0}/
                        {scores.competences?.total || 0} matched
                      </Typography>
                    </ProgressSection>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <ProgressSection>
                      <ProgressLabel>
                        <span>{t('filter.competencesAnnexes')}</span>
                        <span>
                          {formatScore(
                            scores.competencesAnnexes?.percentage || 0
                          )}
                          %
                        </span>
                      </ProgressLabel>
                      <LinearProgress
                        variant="determinate"
                        value={scores.competencesAnnexes?.percentage || 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getScoreColor(
                              scores.competencesAnnexes?.percentage || 0
                            ),
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {scores.competencesAnnexes?.score || 0}/
                        {scores.competencesAnnexes?.total || 0} matched
                      </Typography>
                    </ProgressSection>
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <ProgressSection>
                      <ProgressLabel>
                        <span>{t('filter.languages')}</span>
                        <span>
                          {formatScore(scores.langues?.percentage || 0)}%
                        </span>
                      </ProgressLabel>
                      <LinearProgress
                        variant="determinate"
                        value={scores.langues?.percentage || 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getScoreColor(
                              scores.langues?.percentage || 0
                            ),
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {scores.langues?.score || 0}/
                        {scores.langues?.total || 0} matched
                      </Typography>
                    </ProgressSection>
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <ProgressSection>
                      <ProgressLabel>
                        <span>{t('filter.softskils')}</span>
                        <span>
                          {formatScore(scores.softskils?.percentage || 0)}%
                        </span>
                      </ProgressLabel>
                      <LinearProgress
                        variant="determinate"
                        value={scores.softskils?.percentage || 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getScoreColor(
                              scores.softskils?.percentage || 0
                            ),
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {scores.softskils?.score || 0}/
                        {scores.softskils?.total || 0} matched
                      </Typography>
                    </ProgressSection>
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <ProgressSection>
                      <ProgressLabel>
                        <span>{t('filter.values')}</span>
                        <span>
                          {formatScore(scores.valeurs?.percentage || 0)}%
                        </span>
                      </ProgressLabel>
                      <LinearProgress
                        variant="determinate"
                        value={scores.valeurs?.percentage || 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getScoreColor(
                              scores.valeurs?.percentage || 0
                            ),
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {scores.valeurs?.score || 0}/
                        {scores.valeurs?.total || 0} matched
                      </Typography>
                    </ProgressSection>
                  </Grid>
                </Grid>
              </CardContent>
            </InfoCard>

            {/* Skills Section */}
            <InfoCard>
              <CardContent sx={{ padding: '20px' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faGraduationCap}
                    style={{
                      color: '#667eea',
                      marginRight: '8px',
                      fontSize: '16px',
                    }}
                  />
                  {t('filter.competences')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {candidate?.competences?.slice(0, 8).map((skill, idx) => (
                    <SkillChip
                      key={idx}
                      label={skill.competence}
                      size={sizeChip}
                    />
                  ))}
                  {candidate?.competences?.length > 8 && (
                    <Typography variant="caption" color="text.secondary">
                      +{candidate.competences.length - 8} more
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </InfoCard>

            {/* Languages Section */}
            <InfoCard>
              <CardContent sx={{ padding: '20px' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#1a1a1a',
                    fontWeight: 'bold',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faLanguage}
                    style={{
                      color: '#10b981',
                      marginRight: '8px',
                      fontSize: '16px',
                    }}
                  />
                  {t('filter.languages')}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {candidat?.candidate?.langues?.map((langue, i) => (
                    <LanguageChip
                      key={i}
                      label={
                        langue.langue +
                        ' (' +
                        niveauLanguesList[langue.niveau - 1]?.titre[
                        i18n.language
                        ] +
                        ')'
                      }
                      size={sizeChip}
                    />
                  ))}
                  {candidat?.candidate?.langues?.length > 6 && (
                    <Typography variant="caption" color="text.secondary">
                      +{candidat?.candidate?.langues.length - 6} more
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </InfoCard>

            {/* Skills Section */}
            <InfoCard>
              <CardContent sx={{ padding: '20px' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faChartLine}
                    style={{
                      color: '#f59e0b',
                      marginRight: '8px',
                      fontSize: '16px',
                    }}
                  />
                  {t('filter.competencesAnnexes')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {candidate?.competencesAnnexes
                    ?.slice(0, 8)
                    .map((skill, idx) => (
                      <SkillChip
                        key={idx}
                        label={skill.competence}
                        size={sizeChip}
                        sx={{
                          backgroundColor: 'rgba(245, 158, 11, 0.08)',
                          border: '1px solid rgba(245, 158, 11, 0.2)',
                          color: '#f59e0b',
                          '&:hover': {
                            backgroundColor: 'rgba(245, 158, 11, 0.12)',
                          },
                        }}
                      />
                    ))}
                  {candidate?.competencesAnnexes?.length > 8 && (
                    <Typography variant="caption" color="text.secondary">
                      +{candidate.competencesAnnexes.length - 8} more
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </InfoCard>

            {/* Skills Section */}
            <InfoCard>
              <CardContent sx={{ padding: '20px' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faBrain}
                    style={{
                      color: '#8b5cf6',
                      marginRight: '8px',
                      fontSize: '16px',
                    }}
                  />
                  {t('filter.softskils')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {candidate?.softskils?.slice(0, 8).map((softskil, idx) => (
                    <SkillChip key={idx} label={softskil} size={sizeChip} />
                  ))}
                  {candidate?.softskils?.length > 8 && (
                    <Typography variant="caption" color="text.secondary">
                      +{candidate.softskils.length - 8} more
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </InfoCard>

            {/* Skills Section */}
            <InfoCard>
              <CardContent sx={{ padding: '20px' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faHandshake}
                    style={{
                      color: '#06b6d4',
                      marginRight: '8px',
                      fontSize: '16px',
                    }}
                  />
                  {t('filter.values')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {candidate?.valeurs?.slice(0, 8).map((valeur, idx) => (
                    <ValueChip key={idx} label={valeur} size={sizeChip} />
                  ))}
                  {candidate?.valeurs?.length > 8 && (
                    <Typography variant="caption" color="text.secondary">
                      +{candidate.valeurs.length - 8} more
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </InfoCard>

            {/* Contact Info */}
            {/* <InfoCard>
              <CardContent sx={{ padding: '20px' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {t('profile.contactInformation')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {candidate?.candidat?.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        color="#667eea"
                        fontSize={16}
                      />
                      <Typography variant="body2">
                        {candidate.candidat.email}
                      </Typography>
                    </Box>
                  )}
                  {candidate?.candidat?.telephone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FontAwesomeIcon
                        icon={faPhone}
                        color="#667eea"
                        fontSize={16}
                      />
                      <Typography variant="body2">
                        {candidate.candidat.telephone}
                      </Typography>
                    </Box>
                  )}
                  {candidate?.candidat?.linkedin && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FontAwesomeIcon
                        icon={faGlobe}
                        color="#667eea"
                        fontSize={16}
                      />
                      <Typography variant="body2">
                        {candidate.candidat.linkedin}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </InfoCard> */}
          </Box>
          <Box
            sx={{
              display: step === 2 ? 'flex' : 'none',
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                flexDirection: { xs: 'row', sm: 'row' },
              }}
            >
              {/* <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale={i18n.language}
                        > */}
              {/* <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              rowGap: '5px',
                            }}
                          >
                            <FormLabel className={'labelForm'}>
                              {t('profile.date')}
                            </FormLabel>
          
                            <TextField
                              type="datetime-local"
                              onChange={changeDate}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '5px 0 0 5px',
                                border: '1px solid #9E9E9E',
                                padding: '10px 15px',
                                fontFamily: 'Century Gothic',
                                fontSize: 14,
                                lineHeight: '140%',
                                letterSpacing: '0.2px',
                                color: lightTheme.palette.text.primary,
                                backgroundColor: '#FFFFFF',
                              }}
                            />
          
                            {errorTime && (
                              <Typography className={'error-message'}>
                                {t('errorMessage.date')}
                              </Typography>
                            )}
                          </Grid> */}
              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#374151',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    color="#667eea"
                    fontSize={14}
                  />
                  {t(`contact.date`)}
                </Typography>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={i18n.language}
                >
                  <DatePicker
                    label="Custom input"
                    disablePast
                    inputFormat={'DD/MM/YYYY'}
                    value={dateValue}
                    minDate={tomorrowDate}
                    onChange={(newValue) => {
                      setDateValue(newValue)
                    }}
                    renderInput={({ inputRef, inputProps, InputProps }) => (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            height: '48px',
                            borderRadius: '12px',
                            border: '2px solid #e5e7eb',
                            backgroundColor: '#ffffff',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': {
                              borderColor: '#667eea',
                              boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                            },
                            '&:focus-within': {
                              borderColor: '#667eea',
                              boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.2)',
                            },

                            '& .css-1laqsz7-MuiInputAdornment-root, & .MuiInputAdornment-root':
                            {
                              height: '100%',
                              width: '48px',
                              margin: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              backgroundColor:
                                'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: '0 10px 10px 0',

                              '& .css-1yq5fb3-MuiButtonBase-root-MuiIconButton-root, & .-root-MuiIconButton-root, .MuiIconButton-root':
                              {
                                margin: 0,
                                color: '#FFFFFF !important',
                                '&:hover': {
                                  backgroundColor:
                                    'rgba(255, 255, 255, 0.1)',
                                },
                                '& svg': {
                                  color: '#9a9b9e !important',
                                },
                              },
                            },
                          }}
                        >
                          <input
                            ref={inputRef}
                            {...inputProps}
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '10px 0 0 10px',
                              border: 'none',
                              padding: '12px 16px',
                              fontFamily: 'Century Gothic',
                              fontSize: 14,
                              lineHeight: '140%',
                              letterSpacing: '0.2px',
                              color: lightTheme.palette.text.primary,
                              backgroundColor: 'transparent',
                              outline: 'none',
                            }}
                          />
                          {InputProps?.endAdornment}
                        </Box>
                        <Box
                          className="date-input-overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background:
                              'linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.05) 100%)',
                            borderRadius: '12px',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                            pointerEvents: 'none',
                            '&:hover': {
                              opacity: 1,
                            },
                          }}
                        />
                      </>
                    )}
                  />
                </LocalizationProvider>
                {errorDate && (
                  <Typography
                    className={'error-message'}
                    sx={{
                      color: '#ef4444',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleExclamation} fontSize={12} />
                    {t('errorMessage.date')}
                  </Typography>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#374151',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faClock}
                    color="#667eea"
                    fontSize={14}
                  />
                  {t(`contact.slots`)}
                </Typography>
                {loadingSlots ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 200,
                      backgroundColor: '#f9fafb',
                      borderRadius: '12px',
                      border: '2px dashed #e5e7eb',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <CircularProgress
                        size={32}
                        sx={{
                          color: '#667eea',
                          '& .MuiCircularProgress-circle': {
                            strokeLinecap: 'round',
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          color: '#6b7280',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        {t('home.slots')}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      minHeight: 240,
                      overflowY: 'auto',
                      padding: 1,
                      backgroundColor: '#f9fafb',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',

                      '&::-webkit-scrollbar': {
                        width: 6,
                      },

                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },

                      '&::-webkit-scrollbar-thumb': {
                        background: '#d1d5db',
                        borderRadius: 3,
                      },

                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#9ca3af',
                      },
                    }}
                  >
                    {Object.entries(slots).map(([date, slotTimes]) => (
                      <Box
                        key={date}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#6b7280',
                            letterSpacing: '0.5px',
                            px: 1,
                            py: 0.5,
                            backgroundColor: '#e5e7eb',
                            borderRadius: '6px',
                            textAlign: 'center',
                          }}
                        >
                          {date}
                        </Typography>
                        {slotTimes.length > 0 ? (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              justifyContent: { xs: 'center', sm: 'flex-start' },
                              alignItems: { xs: 'center', sm: 'flex-start' },
                              gap: 1,
                              px: 1,
                            }}
                          >
                            {slotTimes.map((time, index) => {
                              const isSelected =
                                times === time && date === currentdate
                              return (
                                <Button
                                  key={index}
                                  onClick={() => {
                                    handleChangeButtonRadio(time, date)
                                  }}
                                  sx={{
                                    minWidth: 'auto',
                                    px: 2,
                                    py: 1,
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    borderRadius: '8px',
                                    border: '2px solid',
                                    borderColor: isSelected
                                      ? '#667eea'
                                      : '#e5e7eb',
                                    backgroundColor: isSelected
                                      ? '#667eea'
                                      : '#ffffff',
                                    color: isSelected ? '#ffffff' : '#374151',
                                    transition: 'all 0.2s ease',
                                    textTransform: 'none',
                                    boxShadow: isSelected
                                      ? '0 2px 4px rgba(102, 126, 234, 0.3)'
                                      : '0 1px 2px rgba(0, 0, 0, 0.05)',
                                    '&:hover': {
                                      backgroundColor: isSelected
                                        ? '#5a67d8'
                                        : '#f9fafb',
                                      borderColor: isSelected
                                        ? '#5a67d8'
                                        : '#667eea',
                                      transform: 'translateY(-1px)',
                                      boxShadow: isSelected
                                        ? '0 4px 6px rgba(102, 126, 234, 0.4)'
                                        : '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    },
                                  }}
                                >
                                  {time}
                                </Button>
                              )
                            })}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              px: 1,
                              py: 2,
                              textAlign: 'center',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              border: '1px dashed #d1d5db',
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#9ca3af',
                                fontSize: '12px',
                                fontStyle: 'italic',
                              }}
                            >
                              {t('slots.noAvailable')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
                {errorTime && (
                  <Typography
                    className={'error-message'}
                    sx={{
                      color: '#ef4444',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleExclamation} fontSize={12} />
                    {t('errorMessage.timeSlot')}
                  </Typography>
                )}
              </Grid>
              {/* <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              rowGap: '5px',
                            }}
                          >
                            <FormLabel className={'labelForm'}>Heure</FormLabel>
                            <TextField
                              type="time"
                              defaultValue="10:30"
                              onChange={changeTime}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                step: 300, // 5 min
                              }}
                              style={{
                                width: '100%',
                                height: 45,
                                borderRadius: '5px 0 0 5px',
                                border: '1px solid #9E9E9E',
                                padding: '10px 15px',
                                fontFamily: 'Century Gothic',
                                fontSize: 14,
                                letterSpacing: '0.2px',
                                color: lightTheme.palette.text.primary,
                                backgroundColor: '#FFFFFF',
                              }}
                            />
                            <TimePicker
                              value={timeValue}
                              onChange={changeTime}
                              renderInput={({ inputRef, inputProps, InputProps }) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    height: 45,
                                    backgroundColor: lightTheme.palette.primary.b2,
                                    borderRadius: '5px',
          
                                    '& .css-1laqsz7-MuiInputAdornment-root': {
                                      height: '100%',
                                      width: '50px',
                                      margin: 0,
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
          
                                      '& .css-1yq5fb3-MuiButtonBase-root-MuiIconButton-root':
                                        {
                                          margin: 0,
                                          color: '#FFFFFF',
                                        },
                                    },
                                  }}
                                >
                                  <input
                                    ref={inputRef}
                                    {...inputProps}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      borderRadius: '5px 0 0 5px',
                                      border: '1px solid #9E9E9E',
                                      padding: '10px 15px',
                                      fontFamily: 'Century Gothic',
                                      fontSize: 14,
                                      lineHeight: '140%',
                                      letterSpacing: '0.2px',
                                      color: lightTheme.palette.text.primary,
                                      backgroundColor: '#FFFFFF',
                                    }}
                                  />
                                  {InputProps?.endAdornment}
                                </Box>
                              )}
                            />
                            {errorTime && (
                              <Typography className={'error-message'}>
                                Merci de renseigner une plage horraire.
                              </Typography>
                            )}
                          </Grid> */}
              {/* </LocalizationProvider> */}
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                {/* <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#374151',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faVideo}
                    color="#667eea"
                    fontSize={14}
                  />
                  {t('box.videoconferenceLink')}
                </Typography> */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    backgroundColor: '#f0f9ff',
                    borderRadius: '12px',
                    border: '1px solid #bae6fd',
                  }}
                >
                  <FontAwesomeIcon
                    icon={faVideo}
                    color="#0ea5e9"
                    fontSize={16}
                  />
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#0c4a6e',
                      lineHeight: 1.5,
                    }}
                  >
                    {t('videoconference.autoGenerated')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 4 },
            py: { xs: 2, sm: 3 },
            gap: 2,
            justifyContent: 'center',
            backgroundColor: 'rgba(249, 250, 251, 0.8)',
            borderTop: '1px solid rgba(229, 231, 235, 0.8)',
            flexDirection: { xs: 'column', sm: 'row' },
            flexShrink: 0,
            '& > *': {
              width: { xs: '100%', sm: 'auto' },
              m: 0,
            },
          }}
        >
          {step === 2 && (
            <OutlineButton onClick={() => setStep(1)} fullWidth={fullScreen}>
              <FontAwesomeIcon
                icon={faArrowLeft}
                style={{ marginRight: '8px', fontSize: '14px' }}
              />
              {t('button.back')}
            </OutlineButton>
          )}

          {/* Right side - Action buttons */}
          {step === 1 ? (
            <>
              {/* Favorite button for non-admin users */}
              {!isAdmin && (
                <Button
                  onClick={isFavorie ? removeFromFavorie : addToFavorie}
                  fullWidth={fullScreen}
                  disabled={loading}
                  sx={{
                    borderColor: isFavorie ? '#ef4444' : '#ec4899',
                    color: isFavorie ? '#ef4444' : '#ec4899',
                    backgroundColor: 'white',
                    px: 2.5,
                    py: 1.5,
                    fontWeight: 500,
                    borderRadius: '8px',
                    textTransform: 'none',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      backgroundColor: isFavorie
                        ? 'rgba(239, 68, 68, 0.05)'
                        : 'rgba(236, 72, 153, 0.05)',
                      borderColor: isFavorie ? '#dc2626' : '#db2777',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon={isFavorie ? faHeartBroken : faHeart}
                        style={{ marginRight: '8px', fontSize: '14px' }}
                      />
                      {isFavorie
                        ? t('button.removeFavorite')
                        : t('button.addFavorite')}
                    </>
                  )}
                </Button>
              )}

              {/* Match button */}
              <Button
                onClick={() => {
                  ids && ids.includes(candidate?.id) && isAdmin
                    ? addCandidatMatch()
                    : removeCandidatMatch()
                }}
                fullWidth={fullScreen}
                disabled={loadingMatch}
                sx={{
                  borderColor:
                    ids && ids.includes(candidate?.id) && isAdmin
                      ? '#10b981'
                      : '#f59e0b',
                  color:
                    ids && ids.includes(candidate?.id) && isAdmin
                      ? '#10b981'
                      : '#f59e0b',
                  backgroundColor: 'white',
                  px: 2.5,
                  py: 1.5,
                  fontWeight: 500,
                  borderRadius: '8px',
                  textTransform: 'none',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                  '&:hover': {
                    backgroundColor:
                      ids && ids.includes(candidate?.id) && isAdmin
                        ? 'rgba(16, 185, 129, 0.05)'
                        : 'rgba(245, 158, 11, 0.05)',
                    borderColor:
                      ids && ids.includes(candidate?.id) && isAdmin
                        ? '#059669'
                        : '#d97706',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {loadingMatch ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <>
                    <FontAwesomeIcon
                      icon={
                        ids && ids.includes(candidate?.id) && isAdmin
                          ? faChain
                          : faChainBroken
                      }
                      style={{ marginRight: '8px', fontSize: '14px' }}
                    />
                    {ids && ids.includes(candidate?.id) && isAdmin
                      ? t('button.ajouterCandidat')
                      : t('button.retirerCandidat')}
                  </>
                )}
              </Button>

              {/* Schedule button */}
              {!(ids && ids.includes(candidate?.id) && isAdmin) && (
                <GradientButton
                  onClick={() => setStep(2)}
                  fullWidth={fullScreen}
                >
                  <FontAwesomeIcon
                    icon={faCalendarPlus}
                    style={{ marginRight: '8px', fontSize: '14px' }}
                  />
                  {t('button.rdvSuggest')}
                </GradientButton>
              )}
            </>
          ) : (
            <Button
              onClick={handleSubmit(onSubmit)}
              fullWidth={fullScreen}
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: '0 4px 6px rgba(16, 185, 129, 0.25)',
                '&:hover': {
                  background:
                    'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 12px rgba(16, 185, 129, 0.35)',
                },
                '&:disabled': {
                  background: '#d1d5db',
                  color: '#9ca3af',
                  boxShadow: 'none',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <>
                  <FontAwesomeIcon
                    icon={faPaperPlane}
                    style={{ marginRight: '8px', fontSize: '14px' }}
                  />
                  {t('button.sendProposal')}
                </>
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default DetailCandidatEnhanced
